/**
 * Service-worker registration + update control (web-client.md §7). This is DOM/browser glue — it
 * lives in `app/`, never in the DOM-free core. Everything is feature-checked so the client still runs
 * where the SW API is absent (older browsers, private mode, insecure origin).
 *
 * The update decision itself is pure and lives in `core/update` (decideUpdate / decideForcedReload);
 * this module only carries out the mechanics: register, detect a waiting worker, and — on a forced
 * update — post `SKIP_WAITING` then hard-reload once the new worker takes control.
 */
export interface PwaOptions {
  /** Called when a new build has installed and is waiting (soft update → show a "reload" toast). */
  onUpdateReady?: () => void;
  /** Register only in production by default (the dev server has no built /sw.js). */
  enabled?: boolean;
}

interface ServiceWorkerLike {
  postMessage(message: unknown): void;
}

interface RegistrationLike {
  installing: ServiceWorkerLike | null;
  waiting: ServiceWorkerLike | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
  update(): Promise<void>;
}

export class Pwa {
  private registration: RegistrationLike | null = null;
  private reloading = false;

  constructor(private readonly options: PwaOptions = {}) {}

  /** True when the browser exposes the SW API on a secure origin. */
  static supported(): boolean {
    try {
      return 'serviceWorker' in globalThis.navigator;
    } catch {
      return false;
    }
  }

  /** Register the SW (best-effort). Safe to call unconditionally — it no-ops when unsupported. */
  async register(): Promise<void> {
    if (this.options.enabled === false || !Pwa.supported()) return;
    try {
      const container = globalThis.navigator.serviceWorker;
      // A controllerchange after our forced skipWaiting means the new SW is live → hard reload once.
      container.addEventListener('controllerchange', () => this.reloadOnce());
      // Base-aware: under /play/ the worker is /play/sw.js, and its SCOPE is that directory - which is
      // what keeps an app served beside a marketing site from claiming the whole origin.
      const base = import.meta.env.BASE_URL || '/';
      const reg = (await container.register(`${base}sw.js`, {
        scope: base,
      })) as unknown as RegistrationLike;
      this.registration = reg;
      if (reg.waiting) this.options.onUpdateReady?.();
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        // When the new worker finishes installing while one already controls the page, it is waiting.
        (
          installing as unknown as { addEventListener?: (t: string, l: () => void) => void }
        ).addEventListener?.('statechange', () => {
          if (reg.waiting) this.options.onUpdateReady?.();
        });
      });
    } catch {
      // Registration failures are non-fatal: the app runs online without offline/update support.
    }
  }

  /** Poll for a new build (piggybacked on the heartbeat / manifest check, web-client.md §6). */
  async checkForUpdate(): Promise<void> {
    try {
      await this.registration?.update();
    } catch {
      /* ignore */
    }
  }

  /**
   * Forced update (web-client.md §7): tell the waiting worker to skipWaiting, then hard-reload. When
   * no SW is present we still reload so a fresh index.html (network-first) re-handshakes on the new
   * bundle. Idempotent.
   */
  forceReload(): void {
    const waiting = this.registration?.waiting ?? this.registration?.installing ?? null;
    if (waiting) {
      waiting.postMessage({ type: 'SKIP_WAITING' });
      // reloadOnce fires from controllerchange; a timeout guards the case where it never comes.
      globalThis.setTimeout(() => this.reloadOnce(), 1_500);
      return;
    }
    this.reloadOnce();
  }

  private reloadOnce(): void {
    if (this.reloading) return;
    this.reloading = true;
    try {
      globalThis.location.reload();
    } catch {
      /* non-browser env (tests) */
    }
  }
}
